import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { rupiah, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Trash2, Search, MessageCircle, Plus, Mail, Eye, Receipt, UploadCloud } from "lucide-react";

export default function Invoices() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null); // { url, title }
  const [proofDialog, setProofDialog] = useState(null); // { invoice, prevStatus }
  const [uploadingProof, setUploadingProof] = useState(false);
  const prevStatusRef = useRef({});

  const load = async () => {
    const { data } = await api.get("/invoices", { params: { q } });
    setList(data);
    // seed prev status map on first load
    if (Object.keys(prevStatusRef.current).length === 0) {
      const m = {};
      data.forEach((i) => (m[i.id] = i.payment_status));
      prevStatusRef.current = m;
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const del = async (id) => {
    if (!window.confirm("Hapus invoice ini?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice dihapus");
    load();
  };

  const setStatus = async (inv, next) => {
    if (next === inv.payment_status) return;
    setList((cur) => cur.map((x) => (x.id === inv.id ? { ...x, payment_status: next } : x)));
    try {
      await api.patch(`/invoices/${inv.id}/status`, { payment_status: next });
      toast.success(`Status ${inv.invoice_number} → ${next}`);
      // Transitioning to Paid → open proof upload dialog (admin can skip)
      if (next === "Paid" && prevStatusRef.current[inv.id] !== "Paid") {
        setProofDialog({ invoice: { ...inv, payment_status: "Paid" } });
      }
      prevStatusRef.current[inv.id] = next;
    } catch (err) {
      setList((cur) => cur.map((x) => (x.id === inv.id ? { ...x, payment_status: inv.payment_status } : x)));
      toast.error("Gagal update status");
    }
  };

  const uploadProof = async (invoice, file) => {
    if (!file) {
      toast.info("Bukti transfer dilewati — kamu bisa upload nanti");
      setProofDialog(null);
      window.open(receiptUrl(invoice.id), "_blank");
      return;
    }
    setUploadingProof(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/invoices/${invoice.id}/payment-proof`, fd);
      toast.success("Bukti transfer tersimpan");
      setProofDialog(null);
      window.open(receiptUrl(invoice.id), "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload bukti transfer");
    } finally {
      setUploadingProof(false);
    }
  };

  const tok = () => localStorage.getItem("gg_token");
  const pdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf?token=${tok()}`;
  const receiptUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/receipt.pdf?token=${tok()}`;
  const publicUrl = (inv) => inv.access_token ? `${window.location.origin}/i/${inv.id}?t=${inv.access_token}` : pdfUrl(inv.id);
  const publicReceiptUrl = (inv) => inv.access_token ? `${process.env.REACT_APP_BACKEND_URL}/api/public/receipt/${inv.id}/pdf?t=${inv.access_token}` : receiptUrl(inv.id);

  const waSend = (inv) => {
    const phone = (inv.phone || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Halo ${inv.customer_name},\n\nBerikut invoice ${inv.invoice_number} sebesar ${rupiah(inv.total)}.\nLink e-ticket & invoice: ${publicUrl(inv)}\n\nTerima kasih — Gogojalan`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };
  const emailSend = (inv) => {
    const subj = encodeURIComponent(`Invoice ${inv.invoice_number} — Gogojalan`);
    const body = encodeURIComponent(
      `Halo ${inv.customer_name},\n\nBerikut invoice ${inv.invoice_number} sebesar ${rupiah(inv.total)}.\nLink e-ticket & invoice: ${publicUrl(inv)}\n\nTerima kasih — Gogojalan`
    );
    window.location.href = `mailto:${inv.email || ""}?subject=${subj}&body=${body}`;
  };

  const waSendReceipt = (inv) => {
    const phone = (inv.phone || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Halo ${inv.customer_name},\n\nTerima kasih 🙏\nPembayaran invoice ${inv.invoice_number} sebesar ${rupiah(inv.total)} telah kami terima dengan LUNAS.\n\nBukti pembayaran resmi bisa diunduh di sini:\n${publicReceiptUrl(inv)}\n\nSalam,\nGogojalan`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };
  const emailSendReceipt = (inv) => {
    const subj = encodeURIComponent(`Bukti Pembayaran ${inv.invoice_number} — Gogojalan`);
    const body = encodeURIComponent(
      `Halo ${inv.customer_name},\n\nTerima kasih. Pembayaran invoice ${inv.invoice_number} sebesar ${rupiah(inv.total)} telah kami terima dengan LUNAS.\n\nBukti pembayaran resmi bisa diunduh di sini:\n${publicReceiptUrl(inv)}\n\nSalam,\nGogojalan`
    );
    window.location.href = `mailto:${inv.email || ""}?subject=${subj}&body=${body}`;
  };

  const openPreview = (inv, type = "invoice") => {
    setPreview({
      url: type === "receipt" ? receiptUrl(inv.id) : pdfUrl(inv.id),
      title: type === "receipt" ? `Bukti Pembayaran ${inv.invoice_number}` : `Preview ${inv.invoice_number}`,
    });
  };

  const StatusSelect = ({ i }) => (
    <Select value={i.payment_status} onValueChange={(v) => setStatus(i, v)}>
      <SelectTrigger
        data-testid={`inv-status-${i.invoice_number}`}
        className={`h-8 w-[110px] border-0 font-semibold text-xs ${i.payment_status === "Paid" ? "bg-[#e4ecc9] text-[#3d4d1e]" : "bg-[#fef3c7] text-[#92400e]"}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="Unpaid">Unpaid</SelectItem>
        <SelectItem value="Pending">Pending</SelectItem>
        <SelectItem value="Paid">Paid</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Invoice</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Kelola invoice pelanggan.</p>
        </div>
        <Link to="/invoices/new"><Button data-testid="new-invoice-btn" className="bg-[#556b2f] hover:bg-[#3d4d1e]"><Plus size={16} className="mr-1"/> Create Invoice</Button></Link>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Search size={16} className="text-slate-400" />
            <Input data-testid="invoice-search" placeholder="Cari nomor / customer / PNR..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" />
          </div>

          {/* Mobile card view */}
          <div className="md:hidden space-y-3" data-testid="inv-mobile">
            {list.map((i) => (
              <div key={i.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`inv-card-${i.invoice_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mono font-bold text-slate-900 text-sm">{i.invoice_number}</div>
                    <Link to={`/invoices/${i.id}`} className="text-[#556b2f] font-medium truncate block">{i.customer_name}</Link>
                    <div className="text-xs text-slate-500">{shortDate(i.invoice_date)} · {i.airline || "-"}</div>
                  </div>
                  <StatusSelect i={i} />
                </div>
                <div className="mt-2 mono font-bold text-lg text-slate-900">{rupiah(i.total)}</div>
                <div className="mt-2 flex items-center gap-1 flex-wrap justify-end">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPreview(i)} title="Preview" data-testid={`inv-preview-${i.invoice_number}`}><Eye size={16}/></Button>
                  {i.payment_status === "Paid" && (
                    <>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPreview(i, "receipt")} title="Bukti Pembayaran" data-testid={`inv-receipt-${i.invoice_number}`}><Receipt size={16} className="text-[#556b2f]"/></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setProofDialog({ invoice: i })} title="Upload Bukti Transfer" data-testid={`inv-upload-proof-${i.invoice_number}`}><UploadCloud size={16} className="text-slate-600"/></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => waSendReceipt(i)} title="Kirim Bukti Bayar via WhatsApp" data-testid={`inv-wa-receipt-${i.invoice_number}`}><MessageCircle size={16} className="text-[#3d4d1e]"/></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => emailSendReceipt(i)} title="Kirim Bukti Bayar via Email" data-testid={`inv-mail-receipt-${i.invoice_number}`}><Mail size={16} className="text-[#3d4d1e]"/></Button>
                    </>
                  )}
                  <a href={pdfUrl(i.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Unduh PDF"><Download size={16}/></Button></a>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => waSend(i)} title="WhatsApp"><MessageCircle size={16} className="text-[#25D366]"/></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => emailSend(i)} title="Email"><Mail size={16}/></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => del(i.id)} title="Hapus"><Trash2 size={16} className="text-red-600"/></Button>
                </div>
              </div>
            ))}
            {list.length === 0 && <div className="text-center text-slate-400 py-10">Belum ada invoice.</div>}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead>Nomor</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Airline</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((i) => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.invoice_number}`}>
                  <TableCell className="font-semibold mono">{i.invoice_number}</TableCell>
                  <TableCell>{shortDate(i.invoice_date)}</TableCell>
                  <TableCell>
                    <Link to={`/invoices/${i.id}`} className="text-[#556b2f] hover:underline font-medium">{i.customer_name}</Link>
                  </TableCell>
                  <TableCell className="text-slate-600">{i.route}</TableCell>
                  <TableCell className="text-slate-600">{i.airline}</TableCell>
                  <TableCell className="text-right font-semibold mono">{rupiah(i.total)}</TableCell>
                  <TableCell><StatusSelect i={i} /></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" title="Preview PDF" onClick={() => openPreview(i)} data-testid={`inv-preview-${i.invoice_number}`}><Eye size={14}/></Button>
                      {i.payment_status === "Paid" && (
                        <>
                        <Button size="sm" variant="ghost" title="Bukti Pembayaran" onClick={() => openPreview(i, "receipt")} data-testid={`inv-receipt-${i.invoice_number}`}><Receipt size={14} className="text-[#556b2f]"/></Button>
                        <Button size="sm" variant="ghost" title="Upload Bukti Transfer" onClick={() => setProofDialog({ invoice: i })} data-testid={`inv-upload-proof-${i.invoice_number}`}><UploadCloud size={14} className="text-slate-600"/></Button>
                        <Button size="sm" variant="ghost" title="Kirim Bukti Bayar via WhatsApp" onClick={() => waSendReceipt(i)} data-testid={`inv-wa-receipt-${i.invoice_number}`}><MessageCircle size={14} className="text-[#3d4d1e]"/></Button>
                        <Button size="sm" variant="ghost" title="Kirim Bukti Bayar via Email" onClick={() => emailSendReceipt(i)} data-testid={`inv-mail-receipt-${i.invoice_number}`}><Mail size={14} className="text-[#3d4d1e]"/></Button>
                        </>
                      )}
                      <a href={pdfUrl(i.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Unduh PDF"><Download size={14}/></Button></a>
                      <Button size="sm" variant="ghost" title="WhatsApp" onClick={() => waSend(i)}><MessageCircle size={14} className="text-[#25D366]"/></Button>
                      <Button size="sm" variant="ghost" title="Email" onClick={() => emailSend(i)}><Mail size={14} className="text-slate-600"/></Button>
                      <Button size="sm" variant="ghost" title="Hapus" onClick={() => del(i.id)}><Trash2 size={14} className="text-red-600"/></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">Belum ada invoice.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col" data-testid="inv-preview-dialog">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center justify-between gap-2 pr-8">
              <span>{preview?.title}</span>
              {preview?.url && <a href={preview.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download size={14} className="mr-1"/> Unduh</Button></a>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100">
            {preview?.url && <iframe src={preview.url} className="w-full h-full border-0" title="Preview" />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proofDialog} onOpenChange={(v) => !v && setProofDialog(null)}>
        <DialogContent data-testid="proof-dialog">
          <DialogHeader>
            <DialogTitle>Upload Bukti Transfer — {proofDialog?.invoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload bukti transfer (JPG/PNG/PDF). Akan disematkan sebagai QR code di dokumen Bukti Pembayaran dan hanya bisa dilihat oleh admin.</p>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#c9d4a5] rounded-lg py-6 cursor-pointer hover:bg-[#fafcf3] transition" data-testid="proof-upload-label">
              <UploadCloud size={20} className="text-[#556b2f]"/>
              <span className="text-sm font-medium text-slate-700">{uploadingProof ? "Mengupload..." : "Klik untuk pilih file"}</span>
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                disabled={uploadingProof}
                onChange={(e) => uploadProof(proofDialog.invoice, e.target.files?.[0])}
                data-testid="proof-file-input"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => uploadProof(proofDialog.invoice, null)} data-testid="proof-skip">Lewati (upload nanti)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
